import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, Droplets, AlertTriangle, Upload, FileText, Trash2 } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { Plaatkop } from "@/components/Plaatkop";
import {
  AssetStatusBadge, ReplacementBadge, RESPONSIBILITY_LABELS, DotLabel, FilledLabel,
  formatNlDate, formatEuro,
} from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { WerkbonnenTab } from "@/components/WerkbonnenTab";
import { FaultsTab } from "@/components/FaultsTab";
import { InspectionsPanel } from "@/components/InspectionsPanel";
import { AssetDocuments } from "@/components/AssetDocuments";

// No value means no field. A label above a void reads as missing data with a
// missing explanation; leaving it out says the same thing and says it quieter.
const Field = ({ label, value, mono }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-data uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-body text-foreground ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
};

const Placeholder = ({ text }) => (
  <Card className="py-10 text-center text-body text-muted-foreground">{text}</Card>
);

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite, canView } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // §1.1: keep the active tab across a refetch (a document upload triggers
  // load(); Tabs was uncontrolled with defaultValue, so on remount it fell
  // back to "gegevens" and you never saw the PDF you just added).
  const tab = searchParams.get("tab") || "gegevens";
  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/assets/${id}`);
      setA(data);
    } catch (e) {
      setError(getErrorMessage(e, "Asset niet gevonden."));
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading)
    return (
      <div className="page-body space-y-4" data-testid="asset-loading">
        <Skeleton className="h-9 w-48" />
        <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
      </div>
    );
  if (error || !a)
    return (
      <div className="page-body space-y-4">
        <Button variant="ghost" onClick={() => navigate("/assets")}><ArrowLeft size={18} className="mr-2" /> Terug</Button>
        <ErrorState testid="asset-load-error" message={error || "Asset niet gevonden."} onRetry={load} />
      </div>
    );

  const d = a.derived;
  return (
    <div className="page">
      <Plaatkop
        code={a.asset_code}
        srTitle={a.name || `${a.brand || ""} ${a.model || ""}`.trim()}
        srTestid="asset-detail-title"
        subtitle={[a.name, a.location_name || "Zonder locatie", a.ruimte_name, a.spoor, a.category_name].filter(Boolean).join(" · ")}
        backSlot={
          <Button variant="ghost" size="icon" data-testid="asset-back-button" className="-ml-2 text-white hover:bg-plaat-lijn hover:text-white" onClick={() => navigate("/assets")}>
            <ArrowLeft size={18} />
          </Button>
        }
        fields={[
          { label: "Merk", value: a.brand },
          { label: "Type", value: a.model },
          { label: "Serienr", value: a.serial_number, mono: true },
          { label: "Bouwjaar", value: a.purchase_date ? a.purchase_date.slice(0, 4) : null, mono: true },
          { label: "Locatie", value: a.location_name },
          { label: "Ruimte", value: a.ruimte_name },
          { label: "Spoor", value: a.spoor },
        ]}
        collapsibleFields
        lifetime={{
          purchaseDate: a.purchase_date,
          replacementDate: d.replacement_date,
          limitDate: d.technical_limit_date,
        }}
        actions={
          canWrite() && (
            <Button variant="secondary" data-testid="asset-edit-button" onClick={() => navigate(`/assets/${a.id}/bewerken`)}>
              <Pencil size={18} className="mr-2" /> Bewerken
            </Button>
          )
        }
      />

      <div className="page-body space-y-4">
      {/* One badge family: quiet DotLabels for facts, a filled label only when
          something is actually wrong. */}
      <div className="flex flex-wrap items-center gap-3">
        <AssetStatusBadge status={a.status} />
        <ReplacementBadge status={d.replacement_status} date={d.replacement_date} />
        <DotLabel>{a.category_name}</DotLabel>
        <DotLabel>{RESPONSIBILITY_LABELS[a.responsibility]}</DotLabel>
        {d.needs_completion && (
          <span className="inline-flex items-center gap-2">
            <FilledLabel tone="oranje" testid="asset-aanvullen-flag">
              <AlertTriangle size={14} /> Aanvullen
            </FilledLabel>
            <span className="text-data text-muted-foreground">{d.missing_fields.join(", ")}</span>
          </span>
        )}
      </div>

      {d.water_recycling && (
        <div className="flex items-center gap-2 rounded-md border border-wacht-rand bg-wacht-vlak px-3 py-2 text-body text-wacht-tekst">
          <Droplets size={18} /> Deze locatie gebruikt gerecycled water; dit kan de verwachte levensduur verkorten.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="gegevens" data-testid="asset-tab-gegevens">Gegevens</TabsTrigger>
          {canView("werkbonnen") && <TabsTrigger value="werkbonnen" data-testid="asset-tab-werkbonnen">Werkbonnen</TabsTrigger>}
          {canView("storingen") && <TabsTrigger value="storingen">Storingen</TabsTrigger>}
          {canView("keuringen") && <TabsTrigger value="keuringen" data-testid="asset-tab-keuringen">Keuringen</TabsTrigger>}
          <TabsTrigger value="documenten" data-testid="asset-tab-documenten">Documenten</TabsTrigger>
          <TabsTrigger value="qr" data-testid="asset-tab-qr">QR</TabsTrigger>
        </TabsList>

        <TabsContent value="gegevens" className="mt-4 space-y-4">
          <Card className="p-5">
            <h2 className="mb-3 text-body font-semibold">Levensduur &amp; vervanging</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Leeftijd" value={d.age_text} />
              <Field label="Vervangdatum" value={d.replacement_date ? formatNlDate(d.replacement_date) : ""} />
              <Field label="Levensduur (herkomst)" value={d.lifetime_source_text} />
              {d.replacement_from_override && <Field label="Let op" value="Vervangdatum handmatig ingesteld" />}
              {d.technical_limit_date && <Field label="Technische grens (20 jaar)" value={formatNlDate(d.technical_limit_date)} />}
              {d.resolved_lifetime_usage != null && (
                <Field label="Gebruik" value={`${a.usage_counter ?? "?"} / ${d.resolved_lifetime_usage}${d.usage_pct != null ? ` (${d.usage_pct}%)` : ""}`} />
              )}
            </div>
          </Card>
          <Card className="p-5">
            <h2 className="mb-3 text-body font-semibold">Algemeen</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Naam" value={a.name} />
              <Field label="Merk" value={a.brand} />
              <Field label="Model" value={a.model} />
              <Field label="Serienummer" value={a.serial_number} mono />
              <Field label="Locatie" value={a.location_name} />
              <Field label="Ruimte" value={a.ruimte_name} />
              <Field label="Spoor" value={a.spoor} />
              <Field label="Entiteit" value={a.entity} />
              <Field label="Aanschafdatum" value={a.purchase_date ? formatNlDate(a.purchase_date) : ""} />
              <Field label="Aanschafprijs excl. btw" value={a.purchase_price_ex_vat != null ? formatEuro(a.purchase_price_ex_vat) : ""} />
              <Field label="Garantie tot" value={a.warranty_until ? formatNlDate(a.warranty_until) : ""} />
            </div>
          </Card>
          <Card className="p-5">
            <h2 className="mb-3 text-body font-semibold">Leverancier</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Naam" value={a.supplier?.name} />
              <Field label="Contact" value={a.supplier?.contact} />
              <Field label="Telefoon" value={a.supplier?.phone} />
              <Field label="E-mail" value={a.supplier?.email} />
            </div>
            {a.notes && <div className="mt-4"><Field label="Notities" value={a.notes} /></div>}
          </Card>
        </TabsContent>

        {canView("werkbonnen") && <TabsContent value="werkbonnen" className="mt-4"><WerkbonnenTab asset={a} /></TabsContent>}
        {canView("storingen") && <TabsContent value="storingen" className="mt-4"><FaultsTab asset={a} /></TabsContent>}
        {canView("keuringen") && (
          <TabsContent value="keuringen" className="mt-4">
            <InspectionsPanel assetId={a.id} isVehicle={a.category_id === "voertuig"} title={a.category_id === "voertuig" ? "Keuringen (incl. APK)" : "Keuringen"} />
          </TabsContent>
        )}
        <TabsContent value="documenten" className="mt-4"><AssetDocuments asset={a} onChange={() => load({ silent: true })} /></TabsContent>
        <TabsContent value="qr" className="mt-4">
          <Card className="p-5">
            <div className="text-data uppercase tracking-wide text-muted-foreground">QR-code (qr_id)</div>
            <div data-testid="asset-qr-id" className="mt-1 font-mono text-body">{a.qr_id}</div>
            <p className="mt-2 text-body text-muted-foreground">Sticker-vellen met QR-codes print je per locatie via de knop "QR-stickers" op de locatiepagina.</p>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
